import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, TextField, Label, DefaultButton, IDropdownOption, ScrollablePane } from '@fluentui/react';
import { IntegrationCard } from '../IntegrationCard';
import produce from 'immer';
import { disableAllSpellchecksAndAutoCompleteProp } from '../../../../../../helpers/killEvent';
import { ColumnDto, DataverseIntegration, IntegrationEventType, TableDto } from '../../../../../../api/generated/data-contracts';
import { EnvironmentDropdown } from './EnvironmentDropdown';
import { useQuery } from '@tanstack/react-query';
import { DATAVERSE_ENVIRONMENTS_TABLES } from '../../../../../../constants/queryKeys';
import { ApiCalls } from '../../../../../../api/api';
import { ProjectPane } from './Panes/ProjectPane';
import { TaskPane } from './Panes/TaskPane';
import { AssignmentPane } from './Panes/AssignmentPane';
import { ResourcePane } from './Panes/ResourcePane';
import { CalendarPane } from './Panes/CalendarPane';
import { IntegrationCardHandle } from '../IntegrationCardHandle';
import { ProjectTeamPane } from './Panes/ProjectTeamPane';
import { useIntegrations } from '../../../../../../hooks/queries/integrations/useIntegrations';
import { IsMicrosoftEntraIdIntegration } from '../../IntegrationTypeGeneric';
import { GetHostName } from '../../../../../../helpers/trimUrl';

export interface IActualsCanBeEnabled {
    canBeEnabled: boolean;
    message: string;
}

export const DataverseLayout = ({
    integration,
    setIntegration,
    isLoading,
}: {
    integration: DataverseIntegration;
    setIntegration: React.Dispatch<React.SetStateAction<DataverseIntegration>>;
    isLoading: boolean;
}) => {
    const { dataverseUrl } = integration.configuration;

    const [canBeEnabled, setCanBeEnabled] = useState<IActualsCanBeEnabled>({ canBeEnabled: true, message: '' });

    const { integrations } = useIntegrations();

    useEffect(() => {
        if (!integration.subscribedEvents.includes('Assignment')) {
            setCanBeEnabled({
                canBeEnabled: false,
                message: 'Assignment sync needs to be enabled',
            });
        } else if (
            !integration.subscribedEvents.includes('Actuals') &&
            integrations
                .filter(f => f.type === 'Dataverse' && f.id !== integration.id)
                .some((f: DataverseIntegration) => f.subscribedEvents.includes('Actuals'))
        ) {
            setCanBeEnabled({
                canBeEnabled: false,
                message: 'Actual sync is enabled on another dataverse integration',
            });
        } else {
            setCanBeEnabled({
                canBeEnabled: true,
                message: '',
            });
        }
    }, [integrations, integration]);

    const toggleEvent = useCallback(
        (event: IntegrationEventType) => {
            if (event === 'Assignment' && integration.subscribedEvents.includes('Assignment') && integration.subscribedEvents.includes('Actuals')) {
                setIntegration(integration => ({
                    ...integration,
                    subscribedEvents: integration.subscribedEvents.filter(f => f !== 'Actuals'),
                }));
            }
            setIntegration(integration => {
                return {
                    ...integration,
                    subscribedEvents: integration.subscribedEvents.includes(event)
                        ? integration.subscribedEvents.filter(sEvent => sEvent !== event)
                        : [...integration.subscribedEvents, event],
                };
            });
        },
        [setIntegration, integration],
    );

    const projectHandle = useRef<IntegrationCardHandle | null>(null);
    const taskHandle = useRef<IntegrationCardHandle | null>(null);
    const assignmentHandle = useRef<IntegrationCardHandle | null>(null);
    const resourceHandle = useRef<IntegrationCardHandle | null>(null);
    const projectTeamHandle = useRef<IntegrationCardHandle | null>(null);
    const calendarHandle = useRef<IntegrationCardHandle | null>(null);

    const defaultDataverseTables = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: dataverseTables = defaultDataverseTables } = useQuery({
        queryKey: [DATAVERSE_ENVIRONMENTS_TABLES, dataverseUrl],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesList({ id: integration.id, environmentUrl: GetHostName(dataverseUrl) }).then(res =>
                res.data.map(table => ({ key: table.logicalName, text: table.displayName || table.logicalName, data: table } as IDropdownOption<TableDto>)),
            ),
        enabled: Boolean(dataverseUrl),
    });

    const dataverseUrlSet = integration.configuration.dataverseUrl !== '';

    const projectTableAlreadyInUse: boolean = useMemo(() => {
        // Check if another integration has the same project table and is subscribed to Project events
        if (
            integrations
                .find(
                    i =>
                        i.type === 'Dataverse' &&
                        i.configuration.dataverseUrl === integration.configuration.dataverseUrl &&
                        i.configuration.projectTableLogicalName === integration.configuration.projectTableLogicalName &&
                        i.id !== integration.id,
                )
                ?.subscribedEvents.includes('Project')
        ) {
            return true;
        }

        return false;
    }, [integrations, integration.configuration.dataverseUrl, integration.configuration.projectTableLogicalName, integration.id]);

    const canEnableProject: boolean = useMemo(() => {
        if (dataverseUrlSet) {
            if (projectTableAlreadyInUse) return false;

            if (integration.configuration.projectTableLogicalName && integration.configuration.projectIdColumn && integration.configuration.projectNameColumn)
                return true;
        }

        return false;
    }, [
        dataverseUrlSet,
        projectTableAlreadyInUse,
        integration.configuration.projectTableLogicalName,
        integration.configuration.projectIdColumn,
        integration.configuration.projectNameColumn,
    ]);

    const taskTableAlreadyInUse: boolean = useMemo(() => {
        // Check if another integration has the same task table and is subscribed to Task events
        if (
            integrations
                .find(
                    i =>
                        i.type === 'Dataverse' &&
                        i.configuration.dataverseUrl === integration.configuration.dataverseUrl &&
                        i.configuration.taskTableLogicalName === integration.configuration.taskTableLogicalName &&
                        i.id !== integration.id,
                )
                ?.subscribedEvents.includes('Task')
        ) {
            return true;
        }

        return false;
    }, [integrations, integration.configuration.dataverseUrl, integration.configuration.taskTableLogicalName, integration.id]);

    const canEnableTask: boolean = useMemo(() => {
        if (dataverseUrlSet) {
            if (taskTableAlreadyInUse) return false;

            if (integration.subscribedEvents.includes('Project')) {
                if (
                    integration.configuration.taskTableLogicalName &&
                    integration.configuration.taskIdColumn &&
                    integration.configuration.taskNameColumn &&
                    integration.configuration.taskProjectIdColumn
                )
                    return true;
            }

            // If the project table is already set up in another integration, we can enable the task event
            if (projectTableAlreadyInUse) return true;
        }
        return false;
    }, [
        dataverseUrlSet,
        taskTableAlreadyInUse,
        integration.subscribedEvents,
        integration.configuration.taskTableLogicalName,
        integration.configuration.taskIdColumn,
        integration.configuration.taskNameColumn,
        integration.configuration.taskProjectIdColumn,
        projectTableAlreadyInUse,
    ]);

    const assignmentTableAlreadyInUse: boolean = useMemo(() => {
        // Check if another integration has the same assignment table and is subscribed to Assignment events
        if (
            integrations
                .find(
                    i =>
                        i.type === 'Dataverse' &&
                        i.configuration.dataverseUrl === integration.configuration.dataverseUrl &&
                        i.configuration.assignmentTableLogicalName === integration.configuration.assignmentTableLogicalName &&
                        i.id !== integration.id,
                )
                ?.subscribedEvents.includes('Assignment')
        ) {
            return true;
        }

        return false;
    }, [integrations, integration.configuration.dataverseUrl, integration.configuration.assignmentTableLogicalName, integration.id]);

    const canEnableAssignment: boolean = useMemo(() => {
        if (dataverseUrlSet) {
            if (assignmentTableAlreadyInUse) return false;

            if (integration.subscribedEvents.includes('Task')) {
                if (
                    integration.configuration.assignmentTableLogicalName &&
                    integration.configuration.assignmentIdColumn &&
                    integration.configuration.assignmentTaskIdColumn &&
                    integration.configuration.assignmentResourceIdColumn
                )
                    return true;
            }

            // If the task table is already set up in another integration, we can enable the assignment event
            if (taskTableAlreadyInUse) return true;
        }
        return false;
    }, [
        dataverseUrlSet,
        assignmentTableAlreadyInUse,
        integration.subscribedEvents,
        integration.configuration.assignmentTableLogicalName,
        integration.configuration.assignmentIdColumn,
        integration.configuration.assignmentTaskIdColumn,
        integration.configuration.assignmentResourceIdColumn,
        taskTableAlreadyInUse,
    ]);

    const canEnableProjectTeam: boolean = useMemo(() => {
        if (dataverseUrlSet && integration.subscribedEvents.includes('Project')) {
            if (
                integration.configuration.projectTeamTableLogicalName &&
                integration.configuration.projectTeamIdColumn &&
                integration.configuration.projectTeamProjectIdColumn &&
                integration.configuration.projectTeamResourceIdColumn
            )
                return true;
        }

        return false;
    }, [
        dataverseUrlSet,
        integration.subscribedEvents,
        integration.configuration.projectTeamTableLogicalName,
        integration.configuration.projectTeamIdColumn,
        integration.configuration.projectTeamProjectIdColumn,
        integration.configuration.projectTeamResourceIdColumn,
    ]);

    const canEnableResource: boolean = useMemo(() => {
        if (dataverseUrlSet) {
            if (
                integration.configuration.resourceTableLogicalCollectionName &&
                integration.configuration.resourceIdColumn &&
                integration.configuration.resourceNameColumn &&
                integration.configuration.resourceEmailColumn &&
                integration.configuration.resourceUserPrincipalNameColumn &&
                integration.configuration.resourceAzureIdColumn
            )
                return true;
        }

        return false;
    }, [
        dataverseUrlSet,
        integration.configuration.resourceTableLogicalCollectionName,
        integration.configuration.resourceIdColumn,
        integration.configuration.resourceNameColumn,
        integration.configuration.resourceEmailColumn,
        integration.configuration.resourceUserPrincipalNameColumn,
        integration.configuration.resourceAzureIdColumn,
    ]);

    const canEnableCalendar: boolean = useMemo(() => {
        if (dataverseUrlSet) {
            if (
                integration.configuration.calendarTableLogicalName &&
                integration.configuration.calendarIdColumn &&
                integration.configuration.calendarNameColumn
            )
                return true;
        }

        return false;
    }, [
        dataverseUrlSet,
        integration.configuration.calendarTableLogicalName,
        integration.configuration.calendarIdColumn,
        integration.configuration.calendarNameColumn,
    ]);

    const entraIntegrationEnabled = useMemo(
        () => integrations.some(integration => IsMicrosoftEntraIdIntegration(integration) && integration.configuration.enabled),
        [integrations],
    );

    // Helper functions to get tooltip text
    const getProjectTooltip = () => {
        if (canEnableProject) {
            return '';
        }

        if (projectTableAlreadyInUse) {
            return 'This event is already enabled for the same table and environment.';
        }

        return 'All required fields must be filled in.';
    };

    const getTaskTooltip = () => {
        if (canEnableTask) {
            return '';
        }

        if (taskTableAlreadyInUse) {
            return 'This event is already enabled for the same table and environment.';
        }

        return 'All required fields must be filled in.';
    };

    const getAssignmentTooltip = () => {
        if (canEnableAssignment) {
            return '';
        }

        if (assignmentTableAlreadyInUse) {
            return 'This event is already enabled for the same table and environment.';
        }

        return 'All required fields must be filled in.';
    };

    return (
        <Stack horizontal tokens={{ childrenGap: 10 }} {...disableAllSpellchecksAndAutoCompleteProp}>
            <Stack styles={{ root: { paddingLeft: 20, paddingBottom: 20, width: 400 } }}>
                <TextField
                    disabled={isLoading}
                    value={integration.name}
                    label="Name"
                    placeholder='Fx. "PowerPPM Production"'
                    required
                    onChange={(e, val) =>
                        setIntegration(
                            produce(integration => {
                                integration.name = val || '';
                            }),
                        )
                    }
                />
                <EnvironmentDropdown
                    defaultSelectedKey={integration.configuration.dataverseUrl}
                    onChange={(e, option) => {
                        // Trim starting 'https://' from the URL if it exists
                        const hostName = GetHostName(option?.text);
                        setIntegration(
                            produce(integration => {
                                integration.configuration.dataverseUrl = hostName;
                            }),
                        );
                    }}
                    disabled={isLoading}
                />
                <Stack tokens={{ padding: '10px 0' }} horizontal horizontalAlign="start">
                    <DefaultButton
                        onClick={() => {
                            setIntegration(integration => {
                                return {
                                    ...integration,
                                    configuration: {
                                        ...integration.configuration,
                                    },
                                };
                            });
                        }}
                    >
                        Restore default values
                    </DefaultButton>
                </Stack>
            </Stack>

            <div style={{ width: '1px', backgroundColor: 'rgba(0,0,0,0.1)', flexGrow: 1 }} />

            <Stack styles={{ root: { width: '80vw', maxWidth: 1500, minHeight: 720, paddingRight: 10 } }}>
                <Label>Connections</Label>
                <Stack styles={{ root: { position: 'relative', height: '100%', width: '100%' } }}>
                    <ScrollablePane>
                        <Stack styles={{ root: { gap: 10, flexWrap: 'flexGrow' } }} horizontal verticalAlign="start">
                            <IntegrationCard
                                eventType="Project"
                                toggleDisabled={!canEnableProject}
                                enabled={integration.subscribedEvents.includes('Project')}
                                toggleEvent={toggleEvent}
                                tooltip={getProjectTooltip()}
                                ref={projectHandle}
                            >
                                <ProjectPane config={integration.configuration} dataverseTables={dataverseTables} setIntegration={setIntegration} />
                            </IntegrationCard>
                            <IntegrationCard
                                eventType="Task"
                                toggleDisabled={!canEnableTask}
                                enabled={integration.subscribedEvents.includes('Task')}
                                toggleEvent={toggleEvent}
                                tooltip={getTaskTooltip()}
                                ref={taskHandle}
                            >
                                <TaskPane config={integration.configuration} dataverseTables={dataverseTables} setIntegration={setIntegration} />
                            </IntegrationCard>
                            <IntegrationCard
                                eventType="Assignment"
                                toggleDisabled={!canEnableAssignment}
                                enabled={integration.subscribedEvents.includes('Assignment')}
                                toggleEvent={toggleEvent}
                                tooltip={getAssignmentTooltip()}
                                ref={assignmentHandle}
                            >
                                <AssignmentPane
                                    integration={integration}
                                    dataverseTables={dataverseTables}
                                    setIntegration={setIntegration}
                                    toggleEvent={toggleEvent}
                                    canBeEnabled={canBeEnabled}
                                />
                            </IntegrationCard>
                            <IntegrationCard
                                eventType="ProjectTeam"
                                toggleDisabled={!canEnableProjectTeam}
                                enabled={integration.subscribedEvents.includes('ProjectTeam')}
                                toggleEvent={toggleEvent}
                                tooltip={canEnableProjectTeam ? '' : 'All required fields must be filled in.'}
                                ref={projectTeamHandle}
                            >
                                <ProjectTeamPane config={integration.configuration} dataverseTables={dataverseTables} setIntegration={setIntegration} />
                            </IntegrationCard>
                            <IntegrationCard
                                eventType="ResourcesInbound"
                                toggleDisabled={!canEnableResource || entraIntegrationEnabled}
                                enabled={integration.subscribedEvents.includes('ResourcesInbound')}
                                tooltip={
                                    entraIntegrationEnabled
                                        ? 'A Microsoft Entra ID integration is set up with Dataverse Resources. You must disable the Dataverse Resources event there, in order to enable this.'
                                        : ''
                                }
                                toggleEvent={toggleEvent}
                                ref={resourceHandle}
                            >
                                <ResourcePane config={integration.configuration} dataverseTables={dataverseTables} setIntegration={setIntegration} />
                            </IntegrationCard>
                            <IntegrationCard
                                eventType="Calendar"
                                toggleDisabled={!canEnableCalendar}
                                enabled={integration.subscribedEvents.includes('Calendar')}
                                toggleEvent={toggleEvent}
                                tooltip={canEnableCalendar ? '' : 'All required fields must be filled in.'}
                                ref={calendarHandle}
                            >
                                <CalendarPane config={integration.configuration} dataverseTables={dataverseTables} setIntegration={setIntegration} />
                            </IntegrationCard>
                        </Stack>
                    </ScrollablePane>
                </Stack>
            </Stack>
        </Stack>
    );
};
